import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Logo from '../assets/Logo-RB.png';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const ResearchContainer = styled.div`
  padding: 140px 2rem 4rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 80px;
  background: linear-gradient(to bottom, #f4f1e8 0%, #faf9f6 50%, #ffffff 100%);
  min-height: 100vh;
`;

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(90deg, #2d5016 0%, #3d6b1f 50%, #2d5016 100%);
  box-shadow: 0 4px 12px rgba(45, 80, 22, 0.25);
  z-index: 1001;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderLogo = styled.img`
  height: 60px;
  width: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainTitle = styled.h1`
  font-family: 'Chau Philomene One', cursive;
  font-size: 2.4rem;
  color: #ffffff;
  margin: 0;
  letter-spacing: 1px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
  margin: 0.5rem 0 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;



const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(90deg, rgba(239, 244, 240, 0.71), rgba(24, 82, 10, 0.74));
  border-radius: 14px;
  margin-bottom: 2rem;
  box-shadow: 0 6px 18px rgba(22, 62, 16, 0.19), inset 0 1px 0 rgba(255,255,255,0.02);
  backdrop-filter: blur(6px);
  flex-wrap: wrap;
  position: fixed;
  top: 140px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 1100px;
  z-index: 100;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-weight: 600;
  color: #2d5016;
  font-size: 0.95rem;
`;

const FilterButton = styled.button`
  padding: 0.4rem 0.9rem;
  border: 2px solid ${props => props.active ? '#136F07' : 'rgba(244, 241, 232, 0.4)'};
  background: ${props => props.active ? '#136F07' : 'transparent'};
  color: ${props => props.active ? '#f4f1e8' : '#2d5016'};
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: default;
  transition: none;
  &:hover {
    background: ${props => props.active ? '#136F07' : 'transparent'};
    border-color: ${props => props.active ? '#136F07' : 'rgba(244, 241, 232, 0.4)'};
    color: ${props => props.active ? '#f4f1e8' : '#2d5016'};
    transform: none;
    box-shadow: none;
  }
`;

const ResearchGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  max-width: 100%;
`;

const ResearchCard = styled.div`
  background: #faf9f6;
  padding: 1.75rem;
  border-radius: 14px;
  border: 2px solid #e8e4d8;
  box-shadow: 0 4px 12px rgba(45, 80, 22, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 520px;
  
  &:hover {
    transform: translateY(-6px);
    border-color: #196b07f1;
    box-shadow: 0 12px 28px rgba(61, 107, 31, 0.18);
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  background: ${props => {
    const t = props.type;
    if (t === 'Journal') return '#3d6b1f';
    if (t === 'Conference') return '#0d62b6';
    if (t === 'Thesis') return '#8a2be2';
    if (t === 'Capstone') return '#d4a22e';
    if (t === 'Report') return '#2a9d8f';
    return '#6c757d';
  }};
  color: white;
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 1rem;
  align-self: flex-start;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
`;

const CardTitle = styled.h3`
  font-family: 'Chau Philomene One', cursive;
  font-size: 1.1rem;
  color: #2d5016;
  margin: 0 0 1rem 0;
  line-height: 1.3;
  font-weight: 700;
  min-height: 45px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #5a6b52;
  margin-bottom: 0.5rem;
  font-weight: 500;
  height: 25px;
`;

const CardAuthors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.9rem;
  color: #5a6b52;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e8e4d8;
  min-height: 55px;
  
  span:last-child {
    color: #2d5016;
    line-height: 1.4;
    font-weight: 500;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const CardDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #3a3434;
  margin: 0 0 1rem 0;
  font-weight: 400;
  height: 145px;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
`;

const LinkButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.primary ? '#3d6b1f' : '#f4f1e8'};
  color: ${props => props.primary ? '#f4f1e8' : '#2d5016'};
  text-decoration: none;
  border-radius: 8px;
  border: ${props => props.primary ? 'none' : '2px solid #e8e4d8'};
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? '#2d5016' : '#e8e4d8'};
    transform: translateX(3px);
    box-shadow: 0 4px 10px ${props => props.primary ? 'rgba(61, 107, 31, 0.25)' : 'rgba(0, 0, 0, 0.08)'};
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: linear-gradient(180deg, rgba(232,228,216,0.5), rgba(255,255,255,0.8));
  border-radius: 16px;
  border: 2px dashed #e8e4d8;
  box-shadow: 0 4px 12px rgba(45, 80, 22, 0.08);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1.2rem;
  color: #2d5016;
  margin: 0;
  font-weight: 600;
`;

const ResearchPage = () => {
  const [filteredResearches, setFilteredResearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    const fetchPublicResearch = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/research/public`);
        const data = await response.json();
        setFilteredResearches(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('❌ Failed to load research:', err);
        setFilteredResearches([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPublicResearch();
  }, []);

  const sortedResearches = useMemo(() => {
    let sorted = [...filteredResearches];

    if (filterType !== 'all') {
      sorted = sorted.filter(research => research.type === filterType);
    }

    return sorted;
  }, [filteredResearches, filterType]);

  if (loading) {
    return (
      <>
        <HeaderContainer>
          <HeaderLeft>
            <HeaderLogo src={Logo} alt="M.A.K.A.S.A Logo" />
            <HeaderTitle>
              <MainTitle>M.A.K.A.S.A</MainTitle>
              <Subtitle>Research Publications</Subtitle>
            </HeaderTitle>
          </HeaderLeft>
          <ButtonGroup />
        </HeaderContainer>
        <ResearchContainer>
          <LoadingContainer>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
            <p>Loading research...</p>
          </LoadingContainer>
        </ResearchContainer>
      </>
    );
  }

  return (
    <>
      <HeaderContainer>
        <HeaderLeft>
          <HeaderLogo src={Logo} alt="M.A.K.A.S.A Logo" />
          <HeaderTitle>
            <MainTitle>M.A.K.A.S.A</MainTitle>
            <Subtitle>Research Publications</Subtitle>
          </HeaderTitle>
        </HeaderLeft>
        <ButtonGroup />
      </HeaderContainer>

      <ResearchContainer>
        <FilterBar>
          <FilterGroup>
            <FilterLabel>Type:</FilterLabel>
            <FilterButton
              active={filterType === 'all'}
              onClick={() => setFilterType('all')}
            >
              All Types
            </FilterButton>
            <FilterButton
              active={filterType === 'Journal'}
              onClick={() => setFilterType('Journal')}
            >
              Journals
            </FilterButton>
            <FilterButton
              active={filterType === 'Conference'}
              onClick={() => setFilterType('Conference')}
            >
              Conferences
            </FilterButton>
            <FilterButton
              active={filterType === 'Thesis'}
              onClick={() => setFilterType('Thesis')}
            >
              Thesis
            </FilterButton>
          </FilterGroup>
        </FilterBar>

        {sortedResearches.length > 0 ? (
          <ResearchGrid>
            {sortedResearches.map((research) => {
              let authorsDisplay = 'No authors listed';
              if (research.authors) {
                if (typeof research.authors === 'string') {
                  authorsDisplay = research.authors;
                } else if (Array.isArray(research.authors)) {
                  authorsDisplay = research.authors.join(', ');
                } else {
                  authorsDisplay = JSON.stringify(research.authors);
                }
              } else if (research.author) {
                authorsDisplay = research.author;
              }

              const rawType = (research.type || '').trim();
              const typeMap = {
                Journal: { badge: 'Journal', value: 'Journal' },
                Conference: { badge: 'Conference', value: 'Conference' },
                Thesis: { badge: 'Thesis / Dissertation', value: 'Thesis' },
                Capstone: { badge: 'Capstone Project', value: 'Capstone' },
                Report: { badge: 'Research Report', value: 'Report' }
              };

              const { badge: typeBadgeText, value: displayType } =
                typeMap[rawType] || typeMap.Report;

              return (
                <ResearchCard key={research.id}>
                  <TypeBadge type={displayType}>
                    {typeBadgeText}
                  </TypeBadge>

                  <CardTitle>
                    <a
                      href={research.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'inherit',
                        textDecoration: 'none',
                        fontWeight: 'inherit',
                        fontFamily: 'inherit',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {research.title}
                    </a>
                  </CardTitle>

                  <CardMeta>
                    <span>📅 {research.date ? new Date(research.date).toLocaleDateString() : 'No date'}</span>
                  </CardMeta>

                  <CardAuthors>
                    <span style={{ fontWeight: 600, color: '#666' }}>Author:</span>
                    <span>{authorsDisplay}</span>
                  </CardAuthors>

                  <CardDescription>
                    {research.description || 'No description available'}
                  </CardDescription>

                  <LinksContainer>
                    {research.file_path && (
                      <LinkButton
                        href={`${API_BASE}/uploads/${research.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        primary
                      >
                        📥 Download File
                      </LinkButton>
                    )}
                  </LinksContainer>
                </ResearchCard>
              );
            })}
          </ResearchGrid>
        ) : (
          <EmptyState>
            <EmptyIcon>📚</EmptyIcon>
            <EmptyText>No published research available yet.</EmptyText>
            <p style={{ color: '#999', marginTop: '1rem' }}>
              Check back soon for updates!
            </p>
          </EmptyState>
        )}
      </ResearchContainer>
    </>
  );
};

export default ResearchPage;